const pages = {
  about: {
    eyebrow: "Organisation",
    title: "About Gujarat Pharma Union",
    lead: "A professional collective of licensed pharmaceutical traders committed to unity, compliance, patient safety and responsible business.",
    sections: [
      {
        type: "prose",
        heading: "Who We Are",
        body: [
          "Gujarat Pharma Union is a professional collective of licensed pharmaceutical traders committed to unity, compliance, patient safety and responsible business.",
          "GPU brings semi wholesalers, wholesalers, distributors, stockists and super stockists onto a shared, structured platform where business credentials are verified, compliance expectations are clear and support is organised rather than improvised.",
        ],
      },
      {
        type: "bullets",
        heading: "Why We Were Formed",
        intro: "Modern pharmaceutical distribution has become significantly more complex. GPU was formed to respond to that complexity constructively.",
        items: [
          "Increasingly complex, multi-layer supply chains",
          "Counterfeit and spurious product risks entering legitimate channels",
          "Rising compliance and documentation pressure on small and mid-size traders",
          "Fragmented representation across districts",
          "The need for practical batch, invoice and supplier traceability",
          "The need for structured, non-ad-hoc member support",
          "The need for responsible communication and respect for due process",
        ],
      },
      {
        type: "quote",
        heading: "Our Vision",
        body: "To create Gujarat's most trusted, transparent and united pharmaceutical trade ecosystem.",
      },
      {
        type: "quote",
        heading: "Our Mission",
        body: "To empower licensed pharmaceutical traders through verification, compliance, technology, education, representation and structured support.",
      },
      {
        type: "cards",
        heading: "Our Values",
        items: [
          { title: "Integrity", body: "Honest declarations, honest documentation, honest trade." },
          { title: "Compliance", body: "Law and licence conditions are the baseline, not the ceiling." },
          { title: "Unity", body: "A single organised voice instead of scattered individual effort." },
          { title: "Transparency", body: "Verifiable credentials and clear processes." },
          { title: "Fairness", body: "Due process for every business, large or small." },
          { title: "Responsibility", body: "Careful communication and lawful cooperation." },
          { title: "Patient Safety", body: "The final consumer of the medicine comes first." },
        ],
      },
      {
        type: "callout",
        heading: "Positioning Statement",
        body: "GPU does not exist to oppose lawful regulation. GPU exists to strengthen compliant pharmaceutical trade through unity, verification, education, responsible representation and structured support.",
      },
    ],
  },

  objectives: {
    eyebrow: "Mandate",
    title: "Our Objectives",
    lead: "Ten defined objectives that govern every GPU programme, committee and communication.",
    sections: [
      {
        type: "numbered",
        items: [
          { title: "Promote Genuine Pharmaceutical Trade", body: "Encourage ethical and lawful procurement across every tier of distribution." },
          { title: "Strengthen Supplier Due Diligence", body: "Promote verification of licences, GST, invoices and source documentation." },
          { title: "Support Patient Safety", body: "Encourage reporting of suspicious medicines and responsible supply practices." },
          { title: "Protect Compliant Traders", body: "Provide structured support and fair representation for law-abiding businesses." },
          { title: "Improve Traceability", body: "Encourage disciplined batch, invoice and supplier records." },
          { title: "Build Industry Unity", body: "Create district and state-level coordination structures." },
          { title: "Regulatory Awareness", body: "Share official updates, circulars and compliance information." },
          { title: "Training & Education", body: "Conduct workshops on laws, compliance, anti-counterfeit practices and business safety." },
          { title: "Government Representation", body: "Present legitimate industry concerns respectfully before appropriate authorities." },
          { title: "Ethical Trade", body: "Discourage illegal sourcing, false documentation and counterfeit trade." },
        ],
      },
    ],
  },

  membership: {
    eyebrow: "Membership",
    title: "GPU Membership",
    lead: "Membership is open to eligible Gujarat-based licensed pharmaceutical traders, subject to GPU policy and applicable licensing requirements.",
    sections: [
      {
        type: "bullets",
        heading: "Who Can Apply",
        intro: "Eligible Gujarat-based:",
        items: ["Semi Wholesalers", "Wholesalers", "Distributors", "Stockists", "Super Stockists", "Other approved pharmaceutical traders"],
        note: "Subject to GPU policy and applicable licensing requirements.",
      },
      {
        type: "bullets",
        heading: "Basic Eligibility",
        items: [
          "Gujarat business presence",
          "Valid GST registration",
          "Valid applicable Drug Licence",
          "Business KYC",
          "Required agreements",
          "GPU approval",
          "Membership fee",
        ],
      },
      {
        type: "callout",
        heading: "Member Responsibility",
        body: "GPU membership is conditional upon ethical conduct and compliance with GPU policies and applicable laws.",
      },
    ],
  },

  memberBenefits: {
    eyebrow: "Membership",
    title: "Member Benefits",
    lead: "Benefits that are live today, and those clearly marked as forthcoming. Nothing is claimed before it exists.",
    sections: [{ type: "benefitGrid" }],
  },

  memberRegistration: {
    eyebrow: "Process",
    title: "How to Become a GPU Member",
    lead: "A ten-stage verification journey completed inside the official GPU application.",
    sections: [
      {
        type: "numbered",
        items: [
          { title: "Create Account", body: "Mobile OTP and email verification." },
          { title: "Business Profile", body: "Legal name, trade name, business structure and address." },
          { title: "GST Verification", body: "GST details verified through an approved process." },
          { title: "Drug Licence Verification", body: "Applicable drug licence reviewed." },
          { title: "Authorized Person KYC", body: "Submit authorized representative information." },
          { title: "Documents", body: "Upload required business documents." },
          { title: "GPU Review", body: "Maker-checker verification process." },
          { title: "Agreements", body: "Read and eSign GPU declarations and the membership agreement." },
          { title: "Membership Fee", body: "Complete the applicable fee." },
          { title: "Approval", body: "Receive Verified Member status, GPU Member Code and Digital ID." },
        ],
      },
      {
        type: "callout",
        heading: "Before you apply",
        body: "Membership approval is subject to eligibility, document verification, applicable fees, agreements and GPU approval.",
      },
    ],
  },

  declaration: {
    eyebrow: "Membership",
    title: "Membership Declaration",
    lead: "A public summary of the commitments every GPU Member accepts. The complete agreement is presented for eSign inside the GPU application after login.",
    sections: [
      {
        type: "bullets",
        heading: "Members commit to",
        items: [
          "GPU rules, policies and disciplinary framework",
          "Applicable compliance standards under the Drugs and Cosmetics Act and Rules",
          "Ethical pharmaceutical trade and lawful sourcing",
          "Accurate, complete and current information in all declarations",
          "Cooperation with lawful regulatory and investigative processes",
        ],
      },
      {
        type: "callout",
        heading: "Note",
        body: "Full agreement text, schedules and annexures are made available only after authenticated login in the GPU member portal.",
      },
    ],
  },

  vendorNetwork: {
    eyebrow: "Vendors",
    title: "GPU Vendor Network",
    lead: "Vendors may be from Gujarat or other states and can apply to become part of the GPU verified supplier ecosystem.",
    sections: [
      {
        type: "bullets",
        heading: "Eligible Categories",
        items: [
          "Manufacturer",
          "C&F",
          "Distributor",
          "Wholesaler",
          "Super Stockist",
          "Marketing Company",
          "Importer",
          "Authorized Supplier",
          "Other approved categories",
        ],
      },
      {
        type: "cards",
        heading: "Vendor Benefits",
        items: [
          { title: "Verified GPU Vendor Profile", body: "A reviewed, structured credential profile visible to Members." },
          { title: "Digital Vendor ID", body: "A verifiable vendor code for use in trade documentation." },
          { title: "Access to GPU Member Network", body: "Discoverability among licensed Gujarat traders." },
          { title: "Standardized Compliance", body: "One consistent credential set instead of repeated paperwork." },
          { title: "Digital Agreements", body: "eSigned declarations and trade agreements." },
          { title: "Business Visibility", body: "Presence in the Gujarat trade ecosystem." },
          { title: "Vendor Support", body: "A defined channel for vendor queries and disputes." },
          { title: "Regulatory Communication", body: "Relevant circulars and compliance notices." },
          { title: "Structured Relationship Records", body: "Time-stamped records of verification and agreement status." },
        ],
      },
      {
        type: "callout",
        heading: "Vendor Responsibility",
        body: "Vendor registration does not relieve any supplier from its statutory, contractual or product-quality responsibilities.",
      },
    ],
  },

  vendorRegistration: {
    eyebrow: "Process",
    title: "Vendor Registration",
    lead: "An eleven-stage credential and compliance review completed inside the official GPU vendor portal.",
    sections: [
      {
        type: "numbered",
        items: [
          { title: "Account", body: "Create the vendor account with verified mobile and email." },
          { title: "GST", body: "Submit GST registration details." },
          { title: "Drug Licence", body: "Submit applicable drug licence details." },
          { title: "Business KYC", body: "Entity and authorized signatory information." },
          { title: "Supplier Credentials", body: "Category, product scope and trade references." },
          { title: "Authorization Documents", body: "Manufacturer or principal authorizations where applicable." },
          { title: "Compliance Declarations", body: "Declarations on lawful sourcing and documentation." },
          { title: "eSign Agreements", body: "Execute vendor declarations and agreements digitally." },
          { title: "Vendor Fee", body: "Complete the applicable vendor fee." },
          { title: "GPU Review", body: "Maker-checker credential and compliance review." },
          { title: "Verified Vendor Status", body: "Receive GPU Verified Vendor status and Digital Vendor ID." },
        ],
      },
      {
        type: "callout",
        heading: "Before you apply",
        body: "Vendor verification indicates completion of GPU's applicable credential and compliance review process and is not a guarantee of individual products, transactions or commercial performance.",
      },
    ],
  },

  protection: {
    eyebrow: "Protection",
    title: "GPU Protection",
    lead: "GPU Protection gives compliant Members structured access to support when regulatory, legal or commercial pressure arrives.",
    sections: [
      {
        type: "cards",
        heading: "What GPU Protection covers",
        items: [
          { title: "Compliance Support", body: "Guidance on maintaining licence conditions and records." },
          { title: "Legal Support", body: "Structured access to professional legal assistance for eligible matters." },
          { title: "Inspection & Notice Assistance", body: "Help understanding and responding to notices responsibly." },
          { title: "Documentation Guidance", body: "What to keep, how long, and in what form." },
          { title: "Vendor Dispute Assistance", body: "Structured escalation within the GPU ecosystem." },
          { title: "Suspicious Product Reporting", body: "A responsible reporting route for doubtful stock." },
          { title: "Evidence Preservation", body: "Secure, time-stamped retention of trade evidence." },
          { title: "Reputation Support", body: "Measured assistance for eligible trade-reputation issues." },
          { title: "Government Representation", body: "Respectful representation of legitimate industry concerns." },
        ],
      },
      {
        type: "bullets",
        heading: "Legal Support Categories",
        intro: "Eligible matters, addressed privately after Member login:",
        items: [
          "FDCA Inspection",
          "Drug Inspector Visit",
          "Sample Taken",
          "Search / Seizure",
          "Show Cause Notice",
          "Licence Matter",
          "Police / Crime Branch Matter",
          "Product Authenticity Allegation",
          "Vendor Dispute",
          "Media / Reputation Issue",
        ],
        note: "Case-specific matters are never collected through public forms. Members must log in to raise a support request.",
      },
      {
        type: "callout",
        heading: "Important",
        body: "GPU does not obstruct lawful inspection, investigation or regulatory action. The objective is to help eligible Members maintain proper documentation, access professional support and respond responsibly. GPU does not provide legal immunity and cannot stop lawful action by any authority.",
      },
      {
        type: "callout",
        heading: "Positioning Statement",
        body: "GPU does not exist to oppose lawful regulation. GPU exists to strengthen compliant pharmaceutical trade through unity, verification, education, responsible representation and structured support.",
      },
    ],
  },

  safeTrade: {
    eyebrow: "Network",
    title: "GPU Safe Trade Network",
    lead: "Verify the business credentials of your counterparties before beginning trade.",
    sections: [
      {
        type: "flow",
        heading: "How it works",
        items: [
          { title: "Search", body: "Look up a vendor by GPU Vendor Code, firm name or category." },
          { title: "Review", body: "Read declared GST, licence and agreement status with verification dates." },
          { title: "Connect", body: "Engage with the counterparty through structured channels." },
          { title: "Record", body: "Create a time-stamped Safe Source Record for your file." },
          { title: "Trade Responsibly", body: "Retain invoices, batch details and payment trails." },
        ],
      },
      {
        type: "bullets",
        heading: "Safe Source Record",
        intro: "Verified Members can maintain time-stamped records of:",
        items: ["Vendor identity", "GST status", "Drug Licence status", "GPU status", "Agreement status", "Verification date"],
        note: "This record supports due-diligence documentation. It does not guarantee the authenticity of any particular medicine or batch.",
      },
      {
        type: "callout",
        heading: "Verification Disclaimer",
        body: "GPU verification relates to business credentials and compliance status. It is not a guarantee regarding authenticity or quality of every medicine or batch supplied.",
      },
    ],
  },

  antiCounterfeit: {
    eyebrow: "Position",
    title: "Anti-Counterfeit & Responsible Trade",
    lead: "GPU strongly supports strict lawful action against any person knowingly involved in counterfeit, spurious, adulterated or illegal pharmaceutical trade.",
    sections: [
      {
        type: "bullets",
        heading: "GPU stands against knowingly dealing in",
        items: [
          "Counterfeit medicines",
          "Spurious medicines",
          "Adulterated medicines",
          "Illegal supply chains",
          "Falsified invoices",
          "False licences",
        ],
      },
      {
        type: "flow",
        heading: "Recommended Safe Trade Discipline",
        items: [
          { title: "Verify Supplier", body: "Confirm identity and credentials before the first order." },
          { title: "Check Licence", body: "Validate the applicable drug licence and its scope." },
          { title: "Maintain Invoice", body: "Retain complete, accurate purchase invoices." },
          { title: "Record Batch", body: "Capture batch numbers and expiry details." },
          { title: "Preserve Payment Trail", body: "Keep banking records aligned with invoices." },
          { title: "Report Suspicion", body: "Escalate doubtful stock rather than moving it." },
          { title: "Hold & Verify", body: "Quarantine questionable consignments pending verification." },
          { title: "Cooperate Lawfully", body: "Extend full cooperation to authorised officers." },
        ],
      },
      {
        type: "callout",
        heading: "Fair Process",
        body: "Investigation and laboratory verification are important parts of pharmaceutical enforcement. GPU supports evidence-based processes and responsible communication so that actual wrongdoing is identified without prematurely damaging compliant businesses.",
      },
    ],
  },

  institutional: {
    eyebrow: "Engagement",
    title: "Institutional Engagement",
    lead: "GPU welcomes constructive, respectful engagement with regulators, industry and professional institutions.",
    sections: [
      {
        type: "cards",
        heading: "We engage with",
        items: [
          { title: "FDCA Gujarat", body: "Constructive dialogue on compliance awareness and trade practice." },
          { title: "CDSCO", body: "Alignment with central regulatory communication." },
          { title: "Government Departments", body: "Respectful representation of legitimate trade concerns." },
          { title: "Pharmaceutical Manufacturers", body: "Supplier authorization and traceability cooperation." },
          { title: "Trade Bodies", body: "Coordination with allied associations and chambers." },
          { title: "Legal Experts", body: "Professional guidance for compliance and member support." },
          { title: "Healthcare Institutions", body: "Awareness on genuine supply and patient safety." },
        ],
      },
      {
        type: "callout",
        heading: "No False Affiliation",
        body: "GPU is an independent trade body. GPU does not claim Government approval, certification or affiliation, and does not use any Government, FDCA or CDSCO emblem without formal authorisation.",
      },
    ],
  },

  ethics: {
    eyebrow: "Governance",
    title: "GPU Code of Ethical Trade",
    lead: "Eleven core commitments accepted by every GPU Member and Vendor.",
    sections: [
      {
        type: "numbered",
        items: [
          { title: "Lawful Trade", body: "Conduct all trade within the framework of applicable law." },
          { title: "Valid Licences", body: "Hold and maintain every applicable licence and registration." },
          { title: "Genuine Sourcing", body: "Procure only from identifiable, credentialled sources." },
          { title: "No Knowingly Counterfeit Trade", body: "Never knowingly deal in counterfeit, spurious or adulterated medicines." },
          { title: "Accurate Documentation", body: "Maintain true, complete and contemporaneous records." },
          { title: "Traceability", body: "Preserve batch, invoice and supplier trails." },
          { title: "No Falsification", body: "Never create or accept false invoices, licences or declarations." },
          { title: "Patient Safety", body: "Treat the end consumer's safety as the highest priority." },
          { title: "Respectful Regulatory Cooperation", body: "Cooperate fully and courteously with authorised officers." },
          { title: "Responsible Communication", body: "Avoid speculation, defamation and premature public conclusions." },
          { title: "Fair Dealing", body: "Deal fairly with members, vendors, customers and competitors." },
        ],
      },
    ],
  },
};

export default pages;
